import { Toggle } from "@base-ui/react/toggle";
import { ThumbsDownIcon } from "@phosphor-icons/react/dist/csr/ThumbsDown";
import { ThumbsUpIcon } from "@phosphor-icons/react/dist/csr/ThumbsUp";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { useControllableState } from "@volca/ui/hooks/use-controllable-state";
import { cn } from "@volca/ui/lib/utils";

export type FeedbackValue = "up" | "down" | null;

const thumbVariants = cva(
  // A square the icon sits in, the same padding on every side. The gap on
  // the group is the space between the two thumbs; an extra hit area past
  // the button would land on its neighbour. Weight never changes when
  // pressed: colour and fill carry it.
  "inline-flex items-center justify-center gap-1 rounded-md text-subtle tabular-nums transition-[color,background-color,transform] duration-150 ease-out outline-none hover:bg-neutral-subtle-hovered hover:text-ink focus-visible:ring-[3px] focus-visible:ring-focus/50 active:scale-[0.96] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      size: {
        sm: "h-7 min-w-7 px-1.5 text-xs [&_svg]:size-3.5",
        default: "h-8 min-w-8 px-2 text-sm [&_svg]:size-4",
      },
      kind: {
        up: "data-[pressed]:bg-success-subtle data-[pressed]:text-success",
        down: "data-[pressed]:bg-danger-subtle data-[pressed]:text-danger",
      },
    },
    defaultVariants: { size: "default" },
  },
);

export interface FeedbackToggleProps
  extends Omit<React.ComponentProps<"div">, "defaultValue" | "onChange">,
    Pick<VariantProps<typeof thumbVariants>, "size"> {
  value?: FeedbackValue;
  defaultValue?: FeedbackValue;
  /** Pressing the active thumb again takes the rating back (`null`). */
  onValueChange?: (value: FeedbackValue) => void;
  disabled?: boolean;
  /** Accessible names; they also say what the rating means here. */
  upLabel?: string;
  downLabel?: string;
  /** How many people rated it each way, shown beside the thumbs when given. */
  upCount?: number;
  downCount?: number;
}

/**
 * Thumbs up or down on one thing: a finding, an insight, an answer. Two
 * toggles, at most one pressed; pressing the pressed one again clears it.
 * Works uncontrolled (`defaultValue`) or controlled (`value`).
 */
export const FeedbackToggle = ({
  value,
  defaultValue = null,
  onValueChange,
  disabled,
  size,
  upLabel = "Useful",
  downLabel = "Not useful",
  upCount,
  downCount,
  className,
  ...props
}: FeedbackToggleProps) => {
  const [current, setCurrent] = useControllableState<FeedbackValue>({
    value,
    defaultValue,
    onChange: onValueChange,
  });
  const thumb = (kind: "up" | "down") => {
    const Icon = kind === "up" ? ThumbsUpIcon : ThumbsDownIcon;
    const pressed = current === kind;
    const count = kind === "up" ? upCount : downCount;
    return (
      <Toggle
        aria-label={kind === "up" ? upLabel : downLabel}
        pressed={pressed}
        disabled={disabled}
        onPressedChange={(next) => setCurrent(next ? kind : null)}
        className={thumbVariants({ size, kind })}
      >
        <Icon aria-hidden weight={pressed ? "fill" : "regular"} />
        {count !== undefined && count > 0 && <span>{count}</span>}
      </Toggle>
    );
  };
  return (
    <div
      role="group"
      data-slot="feedback-toggle"
      className={cn("inline-flex items-center gap-0.5", className)}
      {...props}
    >
      {thumb("up")}
      {thumb("down")}
    </div>
  );
};
